import { useCallback } from "react";
import {
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "react-query";
import { useAppContext } from "../AppProviders";
import {
  getAllBlueprintInfoMock,
  getBlueprintInfoListMock,
  getReportTypesRootMock,
  getWorkflowsBlueprintsListMock,
} from "../__mocks__/blueprintList";
import { getCheckTypesMock } from "../__mocks__/checkTypes";
import { createBlueprintMock } from "../__mocks__/blueprintOps";

import {
  BehaviorInfoMinimal,
  BlueprintAnnotatedApi,
  BlueprintAssignInput,
  BlueprintAssignedApi,
  BlueprintCopyInput,
  BlueprintEx2,
  BlueprintInfo,
  BlueprintItemTypeInfoMinimal,
  BlueprintItemTypes,
  BlueprintMinimalInfo,
  CheckTypeInfo,
  CreateBlueprintTemplate,
  KeywordGroupExternal,
  KeywordGroupInputCreate,
  KeywordGroupInputUpdate,
  LensTopicsExternal,
  LensTopicsInput,
  LensTopicsUpdateInput,
} from "./Blueprints";
import { XPage, xpageNextOffset } from "./GenericModels";
import { CacheKeys } from "./Cache";
import {
  API_URL,
  buildMockPromise,
  HARDCODED_ACTOR_UUID,
  isMocksEnabled,
} from "./helpers";
import { BehaviorKit, DeleteCompanyBlueprintInput } from "./Companies";

export const CHECK_TYPES_PAGE_SIZE = 10;
export const CHECK_TYPES_QUERY_KEY = ["checkTypes"] as const;
export const CHECK_TYPES_INFINITE_QUERY_KEY = [
  "checkTypes",
  "infinite",
  CHECK_TYPES_PAGE_SIZE,
] as const;

let checkTypesFetchGeneration = 0;
/** Last fully paginated result; used to roll back partial cache on fetch failure. */
let committedCheckTypes: CheckTypeInfo[] = [];

type UseGetCheckTypesOptions = {
  enabled?: boolean;
};

type UseInfiniteCheckTypesOptions = {
  enabled?: boolean;
};

export function invalidateCheckTypesQueries(queryClient: QueryClient): void {
  // Prefix match covers CHECK_TYPES_INFINITE_QUERY_KEY; a second call would
  // re-invalidate and can cancel/restart an in-flight refetch.
  queryClient.invalidateQueries(CHECK_TYPES_QUERY_KEY);
}

/** Clears cached check-type pages and refetches active queries from the first page. */
export function resetCheckTypesQueries(
  queryClient: QueryClient
): Promise<void> {
  // Same prefix match as invalidate — one call is enough for both query shapes.
  return queryClient.resetQueries(CHECK_TYPES_QUERY_KEY);
}

export function useGetBlueprintInfoList(minimal: boolean = false) {
  const { requestApi, cache, refreshCache } = useAppContext();

  const getBlueprintInfoList = async (
    force: boolean = false
  ): Promise<BlueprintInfo[]> => {
    if (isMocksEnabled()) {
      return await getBlueprintInfoListMock();
    }

    if (minimal) {
      if (
        !force &&
        cache.current.blueprintsInfo?.items &&
        Array.isArray(cache.current.blueprintsInfo?.items)
      ) {
        return cache.current.blueprintsInfo.items;
      }
      // some callers only need data from one table, no need to gather all related data for these
      return requestApi
        .post(`${API_URL}/blueprintdal/blueprints/is_template`, { value: true })
        .then((response) => {
          refreshCache(CacheKeys.blueprintsInfo, response.data);
          return response.data;
        });
    }
    return requestApi.get(`${API_URL}/blueprint/template`).then((response) => {
      return response.data;
    });
  };

  const mutationResult = useMutation<
    BlueprintInfo[],
    unknown,
    boolean | undefined
  >((force?: boolean) => getBlueprintInfoList(force), {});
  const { mutate: baseMutate, mutateAsync: baseMutateAsync } = mutationResult;
  const mutate = useCallback(
    (force?: boolean) => baseMutate(force),
    [baseMutate]
  );
  const mutateAsync = useCallback(
    (force?: boolean) => baseMutateAsync(force),
    [baseMutateAsync]
  );

  return {
    ...mutationResult,
    mutate,
    mutateAsync,
  };
}

export function useGetAllBlueprints() {
  const { requestApi, cache, refreshCache } = useAppContext();

  const getBlueprintInfoList = async (): Promise<BlueprintMinimalInfo[]> => {
    if (isMocksEnabled()) {
      return await getAllBlueprintInfoMock();
    }
    if (
      cache.current.blueprints?.items &&
      Array.isArray(cache.current.blueprints?.items)
    ) {
      return cache.current.blueprints.items;
    }
    return requestApi.get(`${API_URL}/blueprints`).then((response) => {
      refreshCache(CacheKeys.blueprints, response.data);
      return response.data;
    });
  };

  const mutationResult = useMutation<BlueprintMinimalInfo[]>(
    () => getBlueprintInfoList(),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetCheckTypes(options: UseGetCheckTypesOptions = {}) {
  const { enabled = true } = options;
  const { requestApi } = useAppContext();
  const queryClient = useQueryClient();

  const getCheckTypes = async (): Promise<CheckTypeInfo[]> => {
    const fetchGeneration = ++checkTypesFetchGeneration;
    const isStaleFetch = () => fetchGeneration !== checkTypesFetchGeneration;
    const cachedCheckTypes = () =>
      queryClient.getQueryData<CheckTypeInfo[]>(CHECK_TYPES_QUERY_KEY) ?? [];
    const restoreCommittedCache = () => {
      if (isStaleFetch()) return;
      queryClient.setQueryData<CheckTypeInfo[]>(
        CHECK_TYPES_QUERY_KEY,
        committedCheckTypes
      );
    };

    try {
      if (isMocksEnabled()) {
        const page = await getCheckTypesMock();
        if (isStaleFetch()) {
          return cachedCheckTypes();
        }
        committedCheckTypes = page.data;
        return page.data;
      }

      let accumulated: CheckTypeInfo[] = [];

      const fetchPage = async (offset: number): Promise<CheckTypeInfo[]> => {
        if (isStaleFetch()) {
          return cachedCheckTypes();
        }

        const response = await requestApi.get(
          `${API_URL}/check-types?offset=${offset}&size=${CHECK_TYPES_PAGE_SIZE}`
        );

        if (isStaleFetch()) {
          return cachedCheckTypes();
        }

        const page: XPage<CheckTypeInfo> = response.data;
        accumulated = accumulated.concat(page.data);
        queryClient.setQueryData<CheckTypeInfo[]>(
          CHECK_TYPES_QUERY_KEY,
          accumulated
        );

        const nextOffset = xpageNextOffset(page, offset);
        if (nextOffset === null) {
          if (isStaleFetch()) {
            return cachedCheckTypes();
          }
          committedCheckTypes = accumulated;
          return accumulated;
        }
        return fetchPage(nextOffset);
      };

      return await fetchPage(0);
    } catch (error) {
      restoreCommittedCache();
      throw error;
    }
  };

  return useQuery<CheckTypeInfo[]>(CHECK_TYPES_QUERY_KEY, getCheckTypes, {
    enabled,
    staleTime: 30_000,
    cacheTime: 5 * 60 * 1000,
    retry: 2,
  });
}

async function fetchCheckTypesPage(
  requestApi: { get: (url: string) => Promise<{ data: XPage<CheckTypeInfo> }> },
  offset: number
): Promise<XPage<CheckTypeInfo>> {
  if (isMocksEnabled()) {
    return getCheckTypesMock(offset, CHECK_TYPES_PAGE_SIZE);
  }

  const response = await requestApi.get(
    `${API_URL}/check-types?offset=${offset}&size=${CHECK_TYPES_PAGE_SIZE}`
  );
  return response.data;
}

/** Page-by-page check types catalog for infinite-scroll UIs (e.g. company assign modal). */
export function useInfiniteCheckTypes(
  options: UseInfiniteCheckTypesOptions = {}
) {
  const { enabled = true } = options;
  const { requestApi } = useAppContext();

  return useInfiniteQuery(
    CHECK_TYPES_INFINITE_QUERY_KEY,
    async ({ pageParam = 0 }) => {
      const requestedOffset = pageParam as number;
      const page = await fetchCheckTypesPage(requestApi, requestedOffset);
      // Preserve request offset for getNextPageParam (legacy stop may echo it
      // while page.offset differs — same pattern as useGetCheckTypes).
      return { ...page, requestedOffset };
    },
    {
      enabled,
      getNextPageParam: (lastPage) =>
        xpageNextOffset(lastPage, lastPage.requestedOffset),
      staleTime: 30_000,
      cacheTime: 5 * 60 * 1000,
      retry: 2,
    }
  );
}

export function useGetWorkflowTemplateBlueprintsList() {
  const { requestApi } = useAppContext();

  const getWorkflowsBlueprintsList = async (): Promise<BlueprintInfo[]> => {
    if (isMocksEnabled()) {
      return await getWorkflowsBlueprintsListMock();
    }
    return requestApi.get(`${API_URL}/blueprint/template`).then((response) => {
      return response.data;
    });
  };

  const mutationResult = useMutation<BlueprintInfo[]>(
    () => getWorkflowsBlueprintsList(),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetWorkflowRootBlueprintsList() {
  const { requestApi } = useAppContext();

  const getWorkflowsBlueprintsList = async (): Promise<BlueprintInfo[]> => {
    if (isMocksEnabled()) {
      return await getWorkflowsBlueprintsListMock();
    }
    return requestApi.get(`${API_URL}/blueprint/root`).then((response) => {
      return response.data;
    });
  };

  const mutationResult = useMutation<BlueprintInfo[]>(
    () => getWorkflowsBlueprintsList(),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetReportTypesRoot() {
  const { requestApi } = useAppContext();

  const getReportTypesRoot = async (): Promise<string[]> => {
    if (isMocksEnabled()) {
      return await getReportTypesRootMock();
    }
    return requestApi
      .get(`${API_URL}/blueprint/root/report_types`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation<string[]>(() => getReportTypesRoot(), {});

  return {
    ...mutationResult,
  };
}

export function useGetBlueprintAnnotated() {
  const { requestApi } = useAppContext();

  const getBlueprint = async (
    blueprintUUID: string
  ): Promise<BlueprintInfo> => {
    if (isMocksEnabled()) {
      return (await getBlueprintInfoListMock())[0];
    }
    return requestApi
      .get(`${API_URL}/blueprint/${blueprintUUID}`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation<BlueprintInfo, unknown, string>(
    (blueprintUUID: string) => getBlueprint(blueprintUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetBlueprintExtendedAnnotated() {
  const { requestApi } = useAppContext();
  interface blueprintArgs {
    blueprintUUID: string;
    companyUUID: string;
    behaviorKitUUID: string;
  }
  const getBlueprint = async ({
    blueprintUUID,
    companyUUID,
    behaviorKitUUID = "None",
  }: blueprintArgs): Promise<BlueprintEx2> => {
    const getRequest =
      behaviorKitUUID !== "None"
        ? `${API_URL}/blueprint/ex2/${blueprintUUID}/${companyUUID}?behavior_kit_uuid=${behaviorKitUUID}`
        : `${API_URL}/blueprint/ex2/${blueprintUUID}/${companyUUID}`;
    return requestApi.get(getRequest).then((response) => {
      return response.data;
    });
  };

  const mutationResult = useMutation<BlueprintEx2, unknown, blueprintArgs>(
    (mutationArgs) => getBlueprint(mutationArgs),
    {}
  );

  return {
    ...mutationResult,
  };
}
export function useGetComputedBlueprintFromWorkflow() {
  const { requestApi } = useAppContext();
  interface blueprintArgs {
    blueprintUUID: string;
    companyUUID: string;
    behaviorKitUUID: string;
    workflowRunUUID: string;
  }
  const getBlueprint = async ({
    blueprintUUID,
    companyUUID,
    behaviorKitUUID = "None",
    workflowRunUUID,
  }: blueprintArgs): Promise<BlueprintEx2> => {
    const getRequest =
      behaviorKitUUID !== "None"
        ? `${API_URL}/workflow_runs/${workflowRunUUID}/computed_blueprint?blueprint_uuid=${blueprintUUID}&company_uuid=${companyUUID}&behavior_kit_uuid=${behaviorKitUUID}`
        : `${API_URL}/workflow_runs/${workflowRunUUID}/computed_blueprint?blueprint_uuid=${blueprintUUID}&company_uuid=${companyUUID}`;
    return requestApi
      .get(getRequest)
      .then((response) => {
        return response.data;
      })
      .catch(() => {
        requestApi
          .get(`${API_URL}/workflowdal/workflowruns/${workflowRunUUID}`)
          .then((response) => {
            return response.data.computed_blueprint;
          });
      });
  };

  const mutationResult = useMutation<BlueprintEx2, unknown, blueprintArgs>(
    (mutationArgs) => getBlueprint(mutationArgs),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetBlueprintAnnotatedList() {
  const { requestApi } = useAppContext();

  const getBlueprints = async (
    blueprintUUIDList: string[]
  ): Promise<BlueprintInfo[]> => {
    if (isMocksEnabled()) {
      return (await getBlueprintInfoListMock()).slice(0, 2);
    }

    const results: any[] = [];

    for (let blueprintUUID of blueprintUUIDList) {
      const result = requestApi
        .get(`${API_URL}/blueprint/${blueprintUUID}`)
        .then((response) => {
          return response.data;
        });

      results.push(result);
    }

    const data = await Promise.all(results);

    return data;
  };

  const mutationResult = useMutation<BlueprintInfo[], unknown, string[]>(
    (blueprintUUIDList: string[]) => getBlueprints(blueprintUUIDList),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useBlueprintCopy() {
  const { requestApi } = useAppContext();

  const blueprintCopy = async (
    input: BlueprintCopyInput[]
  ): Promise<BlueprintAnnotatedApi[]> => {
    const result = [];
    for (let pivot of input) {
      const data = requestApi
        .post(`${API_URL}/blueprint/copy`, pivot)
        .then((response) => {
          return response.data;
        });
      result.push(data);
    }
    return Promise.all(result).then((response) =>
      response.map((item) => {
        if (item[0]) {
          return { ...item[0] };
        }
        return item;
      })
    );
  };

  const mutationResult = useMutation<
    BlueprintAnnotatedApi[],
    unknown,
    BlueprintCopyInput[]
  >((input: BlueprintCopyInput[]) => blueprintCopy(input), {});

  return {
    ...mutationResult,
  };
}

export function useCreateBlueprintTemplate() {
  const { requestApi, refreshCache } = useAppContext();
  const queryClient = useQueryClient();

  const createBlueprintTemplate = async (
    input: CreateBlueprintTemplate
  ): Promise<string> => {
    if (isMocksEnabled()) {
      return await createBlueprintMock(input);
    }

    return requestApi.post(`${API_URL}/blueprint`, input).then((response) => {
      if (response.data) {
        refreshCache(CacheKeys.blueprints);
        refreshCache(CacheKeys.blueprintsInfo);
        invalidateCheckTypesQueries(queryClient);
        return response.data.external_id;
      }
    });
  };

  const mutationResult = useMutation(
    (input: CreateBlueprintTemplate) => createBlueprintTemplate(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useDeleteBlueprint() {
  const { requestApi } = useAppContext();
  const queryClient = useQueryClient();

  const deleteBlueprint = async (blueprintUUID: string): Promise<string> => {
    return requestApi
      .delete(`${API_URL}/blueprints/${blueprintUUID}`)
      .then((response) => {
        if (response.data) {
          invalidateCheckTypesQueries(queryClient);
          return response.data;
        }
      });
  };

  const mutationResult = useMutation(
    (blueprintUUID: string) => deleteBlueprint(blueprintUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useDeleteBlueprintAssign() {
  const { requestApi, refreshCache } = useAppContext();

  const deleteBlueprint = async (
    input: DeleteCompanyBlueprintInput
  ): Promise<string> => {
    return requestApi
      .delete(`${API_URL}/company_blueprints`, input)
      .then((response) => {
        if (response.data) {
          refreshCache(CacheKeys.parentCompanies);
          return response.data;
        }
      });
  };

  const mutationResult = useMutation(
    (input: DeleteCompanyBlueprintInput) => deleteBlueprint(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useDeleteKeywordGroup() {
  const { requestApi } = useAppContext();

  const deleteKeyword = async (keywordUUID: string): Promise<string> => {
    return requestApi
      .delete(`${API_URL}/blueprintdal/keyword_group/${keywordUUID}`)
      .then((response) => {
        if (response.data) {
          return response.data;
        }
      });
  };

  const mutationResult = useMutation(
    (blueprintUUID: string) => deleteKeyword(blueprintUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useUpdateBlueprint() {
  const { requestApi } = useAppContext();
  const queryClient = useQueryClient();

  const updateBlueprint = async (blueprint: BlueprintInfo): Promise<string> => {
    return requestApi
      .put(`${API_URL}/blueprintdal/blueprints/${blueprint.external_id}`, {
        lookback: blueprint.lookback,
        sla_hours: blueprint.sla_hours,
        behaviors: blueprint.behaviors,
        description: blueprint.description,
        modified_by: HARDCODED_ACTOR_UUID, // we need to make sure if this is still needed
      })
      .then((response) => {
        if (response.data) {
          invalidateCheckTypesQueries(queryClient);
          return response.data;
        }
      });
  };

  const mutationResult = useMutation(
    (blueprint: BlueprintInfo) => updateBlueprint(blueprint),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useUpdateBehavior() {
  const { requestApi } = useAppContext();

  const updateBlueprint = async (
    behavior: BehaviorInfoMinimal
  ): Promise<string> => {
    return requestApi
      .put(
        `${API_URL}/blueprintdal/blueprint_behaviors/${behavior.external_id}`,
        {
          is_active: behavior.is_active,
          modified_by: HARDCODED_ACTOR_UUID,
        }
      )
      .then((response) => {
        if (response.data) {
          return response.data;
        }
      });
  };

  const mutationResult = useMutation(
    (behavior: BehaviorInfoMinimal) => updateBlueprint(behavior),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useUpdateBehaviors() {
  const { requestApi } = useAppContext();

  const updateBlueprint = async (
    behaviors: BehaviorInfoMinimal[]
  ): Promise<string[]> => {
    const behaviorsPromises: Promise<string>[] = behaviors.map((pivot) => {
      return requestApi
        .put(
          `${API_URL}/blueprintdal/blueprint_behaviors/${pivot.external_id}`,
          {
            is_active: pivot.is_active,
            modified_by: HARDCODED_ACTOR_UUID,
          }
        )
        .then((response) => {
          if (response.data) {
            return response.data;
          }
        });
    });

    return Promise.all(behaviorsPromises);
  };

  const mutationResult = useMutation(
    (behaviors: BehaviorInfoMinimal[]) => updateBlueprint(behaviors),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useCreateKeywordGroup() {
  const { requestApi } = useAppContext();

  const updateBlueprint = async (
    input: KeywordGroupInputCreate
  ): Promise<string> => {
    return requestApi
      .post(`${API_URL}/blueprint/keyword_group`, input)
      .then((response) => {
        if (response.data) {
          return response.data;
        }
      });
  };

  const mutationResult = useMutation(
    (input: KeywordGroupInputCreate) => updateBlueprint(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useUpdateKeywordGroup() {
  const { requestApi } = useAppContext();

  const updateBlueprint = async (
    input: KeywordGroupInputUpdate
  ): Promise<string> => {
    return requestApi
      .put(`${API_URL}/blueprintdal/keyword_group/${input.external_id}`, {
        ...input,
        modified_by: HARDCODED_ACTOR_UUID,
      })
      .then((response) => {
        if (response.data) {
          return response.data;
        }
      });
  };

  const mutationResult = useMutation(
    (input: KeywordGroupInputUpdate) => updateBlueprint(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

/**
 * Query variant keyed by behavior kit, so `data` always belongs to the kit that was
 * asked for. Use this instead of `useGetKeywordGroups` when the kit can change while
 * the component stays mounted.
 */
export function useKeywordGroupsForBehaviorKit(behaviorKitUUID?: string) {
  const { requestApi } = useAppContext();

  const getKeywordGroups = async (): Promise<KeywordGroupExternal[]> => {
    return requestApi
      .get(`${API_URL}/blueprint/keyword_group/${behaviorKitUUID}`)
      .then((response) => response.data);
  };

  return useQuery<KeywordGroupExternal[]>(
    ["keyword-groups", behaviorKitUUID],
    getKeywordGroups,
    { enabled: !!behaviorKitUUID }
  );
}

export function useGetKeywordGroups() {
  const { requestApi } = useAppContext();

  const getKeywords = async (
    blueprintUUID: string
  ): Promise<KeywordGroupExternal[]> => {
    return requestApi
      .get(`${API_URL}/blueprint/keyword_group/${blueprintUUID}`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (blueprintUUID: string) => getKeywords(blueprintUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetLensTopics() {
  const { requestApi } = useAppContext();

  const getLensTopics = async (
    behaviorKitUUID: string
  ): Promise<LensTopicsExternal[]> => {
    return requestApi
      .get(`${API_URL}/blueprint/lens_topics/${behaviorKitUUID}`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (behaviorKitUUID: string) => getLensTopics(behaviorKitUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useCreateLensTopics() {
  const { requestApi } = useAppContext();

  const createLensTopics = async (
    input: LensTopicsInput
  ): Promise<LensTopicsExternal> => {
    return requestApi
      .post(`${API_URL}/blueprint/lens_topics`, input)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: LensTopicsInput) => createLensTopics(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useUpdateLensTopics() {
  const { requestApi } = useAppContext();

  const updateLensTopics = async (
    input: LensTopicsUpdateInput
  ): Promise<LensTopicsExternal> => {
    const { external_id, ...payload } = input;
    return requestApi
      .put(`${API_URL}/blueprint/lens_topics/${external_id}`, payload)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: LensTopicsUpdateInput) => updateLensTopics(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useDeleteLensTopics() {
  const { requestApi } = useAppContext();

  const deleteLensTopics = async (
    lensTopicsUUID: string
  ): Promise<LensTopicsExternal> => {
    return requestApi
      .delete(`${API_URL}/blueprint/lens_topics/${lensTopicsUUID}`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (lensTopicsUUID: string) => deleteLensTopics(lensTopicsUUID),
    {}
  );

  return {
    ...mutationResult,
  };
}

export type GenerateLensTopicsInput = {
  id: string;
  keywords: string[];
};

export function useGenerateLensTopics() {
  const { requestApi } = useAppContext();

  const generateLensTopics = async (
    input: GenerateLensTopicsInput
  ): Promise<unknown> => {
    return requestApi
      .post(`${API_URL}/topic-model`, input)
      .then((response) => response.data);
  };

  return useMutation(
    (input: GenerateLensTopicsInput) => generateLensTopics(input),
    {}
  );
}

export function useGetBehaviorKits() {
  const { requestApi } = useAppContext();

  const getBehaviorKits = async (
    companyUUID: string
  ): Promise<BehaviorKit[]> => {
    return requestApi
      .post(`${API_URL}/blueprintdal/behavior_kit/company_uuid`, {
        value: companyUUID,
        is_partial: false,
      })
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (companyUUID: string) => getBehaviorKits(companyUUID),
    {}
  );
  return {
    ...mutationResult,
  };
}

export function usePostBlueprintTask() {
  const { requestApi } = useAppContext();

  const postBlueprintTask = async (input: {
    taskKey: string;
    taskDisplay: string;
  }): Promise<BehaviorKit[]> => {
    return requestApi
      .post(
        `${API_URL}/blueprint/update_create_blueprint_task?task_key=${input.taskKey}&task_display=${input.taskDisplay}`
      )
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: { taskKey: string; taskDisplay: string }) =>
      postBlueprintTask(input),
    {}
  );
  return {
    ...mutationResult,
  };
}

export function useSyncBlueprintTask() {
  const { requestApi } = useAppContext();

  const syncBlueprintTask = async (): Promise<string> => {
    return requestApi
      .post(`${API_URL}/blueprint/sync_blueprint_task`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(() => syncBlueprintTask(), {});
  return {
    ...mutationResult,
  };
}

export function useBlueprintAssign() {
  const { requestApi, refreshCache } = useAppContext();

  const blueprintAssign = async (
    input: BlueprintAssignInput[]
  ): Promise<BlueprintAssignedApi[]> => {
    const result = [];
    for (let pivot of input) {
      const data = requestApi
        .post(`${API_URL}/company_blueprints`, pivot)
        .then((response) => {
          return response.data;
        });
      result.push(data);
    }
    return Promise.all(result).then((response) => {
      refreshCache(CacheKeys.parentCompanies);
      return response.map((item) => {
        if (item[0]) {
          return { ...item[0] };
        }
        return item;
      });
    });
  };

  const mutationResult = useMutation<
    BlueprintAssignedApi[],
    unknown,
    BlueprintAssignInput[]
  >((input: BlueprintAssignInput[]) => blueprintAssign(input), {});

  return {
    ...mutationResult,
  };
}

export function itemTypeToUI(itemType: BlueprintItemTypeInfoMinimal): string {
  switch (itemType.name) {
    case BlueprintItemTypes.ScrapeInstagramPosts:
      return "Instagram Posts";
    case BlueprintItemTypes.ScrapeInstagramReels:
      return "Instagram Reels";
    default:
      return itemType.name;
  }
}

export function useGetWorkflowStepNames() {
  const { requestApi } = useAppContext();

  const getWorkflowStepNames = async (): Promise<string[]> => {
    if (isMocksEnabled()) {
      return await buildMockPromise<string[]>([
        "PersonScrape1",
        "ModelPredict",
        "AutoRate",
        "ManualRate",
        "PersonEnrichment",
        "ProfilePostScrape",
        "ModelPredict2",
        "PostsAutoRate",
        "PostManualRate",
        "FinalReportQA",
        "PDFGeneration",
      ]);
    }
    return requestApi
      .get(`${API_URL}/workflowdal/workflow_step_names`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation<string[]>(
    () => getWorkflowStepNames(),
    {}
  );

  return {
    ...mutationResult,
  };
}
